<template>
  <div class="contact-page">
    <header class="contact-hero">
      <div class="contact-glow" aria-hidden="true"></div>
      <div class="lp-container contact-hero__inner">
        <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ $t('contact.eyebrow') }}</div>
        <h1 class="lp-h2 contact-title lp-balance">{{ $t('contact.title') }}</h1>
        <p class="lp-lead">{{ $t('contact.lead') }}</p>
      </div>
    </header>

    <section class="lp-container contact-body">
      <form class="lp-card contact-card" @submit.prevent="send">
        <div class="contact-row">
          <label class="contact-field">
            <span class="contact-label">{{ $t('contact.name') }}</span>
            <input v-model.trim="name" type="text" class="contact-input" required />
          </label>
          <label class="contact-field">
            <span class="contact-label">{{ $t('contact.email') }}</span>
            <input v-model.trim="email" type="email" class="contact-input" required />
          </label>
        </div>

        <label class="contact-field">
          <span class="contact-label">{{ $t('contact.subject') }}</span>
          <input v-model.trim="subject" type="text" class="contact-input" required />
        </label>

        <label class="contact-field">
          <span class="contact-label">{{ $t('contact.message') }}</span>
          <textarea v-model.trim="message" rows="7" class="contact-input contact-textarea" required></textarea>
        </label>

        <p v-if="sent" class="contact-sent">{{ $t('contact.sent') }}</p>

        <div class="contact-actions">
          <p class="contact-direct">
            {{ $t('contact.direct') }}
            <a :href="'mailto:' + contactEmail">{{ contactEmail }}</a>
          </p>
          <button type="submit" class="lp-btn lp-btn--primary" :disabled="!name || !email || !subject || !message">
            {{ $t('contact.send') }}
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
/**
 * Página de contacto con la estética de la landing (tokens lp-* de
 * landing.css) y el mismo patrón de páginas interiores que
 * views/politiques/LegalPageLayout.vue: el AppBar es fijo de 64px y v-main
 * no lo compensa, así que el hero lo despeja con su padding-top.
 *
 * No hay endpoint de contacto en la API de Nukli: el envío compone un
 * mailto a hola@nukliapp.com con los campos del formulario.
 */
const CONTACT_EMAIL = 'hola@nukliapp.com';

export default {
  name: 'Contact',
  data: () => ({
    name: '',
    email: '',
    subject: '',
    message: '',
    sent: false,
    contactEmail: CONTACT_EMAIL,
    translations: {
      en: {
        contact: {
          eyebrow: 'Contact',
          title: 'Get in touch',
          lead: 'Questions, feedback or trouble with Nukli? Send us a message and we will get back to you as soon as possible.',
          name: 'Name',
          email: 'Email',
          subject: 'Subject',
          message: 'Message',
          send: 'Send message',
          direct: 'Or write to us directly at',
          sent: 'Your email app should now open with the message ready to send.'
        }
      },
      es: {
        contact: {
          eyebrow: 'Contacto',
          title: 'Habla con nosotros',
          lead: '¿Dudas, sugerencias o problemas con Nukli? Envíanos un mensaje y te responderemos lo antes posible.',
          name: 'Nombre',
          email: 'Correo electrónico',
          subject: 'Asunto',
          message: 'Mensaje',
          send: 'Enviar mensaje',
          direct: 'O escríbenos directamente a',
          sent: 'Se abrirá tu aplicación de correo con el mensaje listo para enviar.'
        }
      },
      ca: {
        contact: {
          eyebrow: 'Contacte',
          title: 'Parla amb nosaltres',
          lead: 'Dubtes, suggeriments o problemes amb Nukli? Envia\'ns un missatge i et respondrem tan aviat com puguem.',
          name: 'Nom',
          email: 'Correu electrònic',
          subject: 'Assumpte',
          message: 'Missatge',
          send: 'Enviar missatge',
          direct: 'O escriu-nos directament a',
          sent: 'S\'obrirà la teva aplicació de correu amb el missatge a punt per enviar.'
        }
      }
    }
  }),
  methods: {
    $t(key) {
      const lang = this.$root.currentLanguage || 'ca';
      const dict = this.translations[lang] || this.translations.en;
      const translation = key.split('.').reduce((obj, i) => obj && obj[i], dict);
      return translation || key.split('.').reduce((obj, i) => obj && obj[i], this.translations.en) || key;
    },
    send() {
      const body = `${this.message}\n\n—\n${this.name}\n${this.email}`;
      const url =
        `mailto:${CONTACT_EMAIL}` +
        `?subject=${encodeURIComponent(this.subject)}` +
        `&body=${encodeURIComponent(body)}`;
      window.location.href = url;
      this.sent = true;
    }
  }
};
</script>

<style scoped>
.contact-page {
  background: var(--lp-bg);
  min-height: 100vh;
}

/* 64px del AppBar fijo + aire, mismo lenguaje visual que las páginas legales */
.contact-hero {
  position: relative;
  padding: 128px 0 24px;
  overflow: hidden;
}
.contact-glow {
  position: absolute;
  top: -160px;
  left: 50%;
  transform: translateX(-50%);
  width: 1100px;
  height: 560px;
  background: radial-gradient(60% 60% at 50% 0%, var(--lp-accent-weak) 0%, rgba(0, 0, 0, 0) 70%);
  pointer-events: none;
  z-index: 0;
}
.contact-hero__inner {
  position: relative;
  z-index: 1;
  max-width: 820px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}
.contact-title { margin: 0; }

.contact-body {
  max-width: 820px;
  padding-bottom: 112px;
}
.contact-card {
  padding: 26px 28px;
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.contact-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.contact-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.contact-label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--lp-ink-2);
}
.contact-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--lp-line-strong);
  border-radius: 12px;
  background: var(--lp-surface);
  color: var(--lp-ink);
  font-family: inherit;
  font-size: 0.98rem;
  line-height: 1.5;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.contact-input:focus {
  outline: none;
  border-color: var(--lp-accent);
  box-shadow: 0 0 0 3px var(--lp-accent-weak);
}
.contact-textarea { resize: vertical; }

.contact-sent {
  color: var(--lp-accent);
  font-size: 0.95rem;
  margin: 0;
}

.contact-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.contact-direct {
  color: var(--lp-muted);
  font-size: 0.95rem;
  margin: 0;
}
.contact-direct a {
  color: var(--lp-accent);
  text-decoration: none;
}
.contact-direct a:hover { text-decoration: underline; }

.lp-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 599px) {
  .contact-hero { padding: 104px 0 12px; }
  .contact-card { padding: 20px 18px; }
  .contact-row { grid-template-columns: 1fr; }
}
</style>
